import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Check, X } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { SaveBadge } from "@/components/SaveBadge";

const FEATURES = [
  { key: "feat.browse", free: true, pro: true },
  { key: "feat.details", free: true, pro: true },
  { key: "feat.blankPdf", free: true, pro: true },
  { key: "feat.fillOnline", free: false, pro: true },
  { key: "feat.ai", free: false, pro: true },
  { key: "feat.download", free: false, pro: true },
  { key: "feat.save", free: false, pro: true },
  { key: "feat.print", free: false, pro: true },
  { key: "feat.unlimited", free: false, pro: true },
];

export default function Pricing() {
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [annual, setAnnual] = useState(false);
  const [plans, setPlans] = useState([]);
  const [singlePrice, setSinglePrice] = useState(3.95);
  const [busy, setBusy] = useState(null);

  useEffect(() => { api.get("/plans").then(({ data }) => { setPlans(data.plans); if (data.single_form_price) setSinglePrice(data.single_form_price); }).catch(() => {}); }, []);

  const price = (p) => plans.find((x) => x.lookup_key === p);
  const activeKey = annual ? "pro_yearly" : "pro_monthly";
  const proPrice = price(activeKey);

  const subscribe = async () => {
    if (!user) { navigate("/login"); return; }
    setBusy(activeKey);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: activeKey, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not start checkout");
      setBusy(null);
    }
  };

  const subscribed = user && user.subscription_status === "active";

  return (
    <div>
      <Helmet>
        <title>Pricing — Free, Pro & Pay-Per-Form | NC Legal Forms</title>
        <meta name="description" content="Simple pricing for North Carolina court forms: start free, go Pro (monthly or annual) for unlimited forms, or unlock a single form for $3.95. Fill, autofill, print and download online." />
        <link rel="canonical" href={`${process.env.REACT_APP_BACKEND_URL || ""}/pricing`} />
      </Helmet>
      <Navbar />
      <section className="max-w-7xl mx-auto px-6 pt-16 pb-8 text-center">
        <p className="overline text-accent mb-4">{t("pricing.eyebrow")}</p>
        <h1 className="font-serif text-5xl sm:text-6xl tracking-tighter mb-4">{t("pricing.title")}</h1>
        <p className="text-muted-foreground max-w-xl mx-auto">{t("pricing.subtitle")}</p>

        <div className="inline-flex items-center gap-3 mt-8 border border-border rounded-sm p-1 bg-secondary">
          <button data-testid="billing-monthly" onClick={() => setAnnual(false)}
            className={`px-5 py-2 text-sm rounded-sm transition-colors ${!annual ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>{t("pricing.monthly")}</button>
          <button data-testid="billing-annual" onClick={() => setAnnual(true)}
            className={`px-5 py-2 text-sm rounded-sm transition-colors ${annual ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
            {t("pricing.annual")} <span className="text-accent font-semibold">{t("pricing.save20")}</span></button>
        </div>
        <p className="text-sm text-muted-foreground mt-6" data-testid="trial-banner">
          ✦ {t("pricing.trial")}
        </p>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-16 grid md:grid-cols-3 gap-6 items-stretch">
        {/* Free */}
        <div className="grid-card p-8 flex flex-col" data-testid="plan-free">
          <p className="overline text-muted-foreground">{t("pricing.free")}</p>
          <p className="font-serif text-5xl mt-3">$0</p>
          <p className="text-sm text-muted-foreground mt-1 mb-6">{t("pricing.freeSub")}</p>
          <ul className="space-y-3 text-sm flex-1">
            {FEATURES.map((f) => (
              <li key={f.key} className="flex items-start gap-3">
                {f.free ? <Check size={18} className="text-accent shrink-0 mt-0.5" /> : <X size={18} className="text-muted-foreground/40 shrink-0 mt-0.5" />}
                <span className={f.free ? "" : "text-muted-foreground/50"}>{t(f.key)}</span>
              </li>
            ))}
          </ul>
          <Button variant="outline" disabled className="rounded-sm h-11 mt-8 w-full">{t("pricing.includedDefault")}</Button>
        </div>

        {/* Pro */}
        <div className="relative bg-primary text-primary-foreground p-8 flex flex-col" data-testid="plan-pro">
          <span className="absolute top-0 right-0 bg-accent text-accent-foreground text-xs px-3 py-1 overline">{t("pricing.recommended")}</span>
          <p className="overline text-accent">{t("pricing.pro")}</p>
          <div className="flex items-end gap-2 mt-3">
            <p className="font-serif text-5xl">${proPrice ? proPrice.amount : (annual ? "590" : "59")}</p>
            <p className="text-primary-foreground/60 mb-2 text-sm">/ {annual ? t("pricing.perYear") : t("pricing.perMonth")}</p>
          </div>
          <p className="text-sm text-primary-foreground/60 mt-1 mb-6">{annual ? t("pricing.billedAnnual") : t("pricing.billedMonthly")}</p>
          <ul className="space-y-3 text-sm flex-1">
            {FEATURES.map((f) => (
              <li key={f.key} className="flex items-start gap-3">
                <Check size={18} className="text-accent shrink-0 mt-0.5" /><span>{t(f.key)}</span>
              </li>
            ))}
          </ul>
          {subscribed ? (
            <Button disabled className="rounded-sm h-11 mt-8 w-full bg-accent text-accent-foreground">{t("pricing.isPro")}</Button>
          ) : (
            <Button onClick={subscribe} disabled={busy} data-testid="subscribe-btn"
              className="rounded-sm h-11 mt-8 w-full bg-accent text-accent-foreground hover:bg-white hover:text-primary transition-colors">
              {busy ? t("pricing.redirecting") : user ? t("pricing.upgrade") : t("pricing.getPro")}
            </Button>
          )}
        </div>

        {/* Pay per document */}
        <div className="grid-card p-8 flex flex-col" data-testid="plan-single">
          <p className="overline text-muted-foreground">Pay per document</p>
          <div className="flex items-end gap-2 mt-3">
            <p className="font-serif text-5xl">${singlePrice}</p>
            <p className="text-muted-foreground mb-2 text-sm">/ form</p>
          </div>
          <p className="text-sm text-muted-foreground mt-1 mb-6">One-time unlock — no subscription.</p>
          <ul className="space-y-3 text-sm flex-1">
            <li className="flex items-start gap-3"><Check size={18} className="text-accent shrink-0 mt-0.5" /><span>Fill this one form online</span></li>
            <li className="flex items-start gap-3"><Check size={18} className="text-accent shrink-0 mt-0.5" /><span>AI-assisted completion</span></li>
            <li className="flex items-start gap-3"><Check size={18} className="text-accent shrink-0 mt-0.5" /><span>Download the completed PDF</span></li>
            <li className="flex items-start gap-3"><Check size={18} className="text-accent shrink-0 mt-0.5" /><span>Save &amp; print this form</span></li>
            <li className="flex items-start gap-3"><X size={18} className="text-muted-foreground/40 shrink-0 mt-0.5" /><span className="text-muted-foreground/50">Access to other forms</span></li>
          </ul>
          <Button variant="outline" onClick={() => navigate("/forms")} data-testid="single-browse-btn"
            className="rounded-sm h-11 mt-8 w-full">Pick a form to unlock</Button>
          <button onClick={() => navigate("/bundle")} data-testid="bundle-cta"
            className="mt-3 w-full text-sm text-accent font-semibold hover:underline">
            Need a few? Get any 3 forms for $9.95 → <SaveBadge testid="pricing-bundle-save-badge" className="ml-1 align-middle" />
          </button>
        </div>
      </section>
      <Footer />
    </div>
  );
}
